import {useConfig} from '@aboutyou/config';

import {FreeDeliverySection} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/basket_mov.types';
import {ProductImageTypes} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/product_images.types';
import {ProductTile} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/product_tile.types';
import {ProductTilePreferences} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/product_tile_preferences.types';
import {AddItemResponseStatus_Success_Section} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/basket/v1/add_item_status.types';
import {useTranslation} from '@aboutyou/i18n';
import {originClick} from '@aboutyou/legacy-tracking-sdk';
import {
  useTrackFeature,
  useTrackFeatureInViewImpression,
  useTrackOriginClick,
} from '@aboutyou/tracking';
import {Accordion, ProductImage, Text} from '@aboutyou/ui-components';
import React from 'react';
import styled from 'styled-components';
import {useMiniAdpDialogOpenedState} from '../../../../features/MiniADPDialog/useMiniAdpDialogOpenedState';
import {ProductMiniAdp} from '../../../../features/ProductMiniAdp/ProductMiniAdp';
import {
  AddToBasketPayload,
  StreamProductTileDeprecated,
} from '../../../../features/ProductStream/Tiles/StreamProductTileDeprecated';
import {ProductList} from '../../../../features/Recommendations/deprecated/shared';
import {SizeSelectionOpenedStateKey} from '../../../../features/SizeSelectionFlyout/hooks/useSizeSelectionOpenedState';
import {RecommendationsComponentProps} from '../BasketMOVView';

export const BasketMOVAccordion: React.FC<RecommendationsComponentProps> = ({
  movUpsellingRecommendationSection,
  onAddToBasketSuccess,
  refetchBasketSection,
}) => {
  const [isOpen, setIsOpen] = React.useState(false);

  const {
    isOpen: isFlyoutOpen,
    close: closeFlyout,
    open: openFlyout,
    productId,
    selectedSize,
    setSelectedSize,
  } = useMiniAdpDialogOpenedState();

  const {device} = useConfig();
  const trackFeature = useTrackFeature();

  const {ref} = useTrackFeatureInViewImpression<HTMLDivElement>({
    label: 'mov_reco',
    name: 'mov_reco',
  });
  const trackOriginClick = useTrackOriginClick();

  const trackProductsOriginClick = trackOriginClick(
    movUpsellingRecommendationSection.promotionTracking.name as originClick,
  );

  const {t} = useTranslation();

  const onClickAddToBasket = (productTile: AddToBasketPayload) => {
    return openFlyout({productId: productTile.product.id});
  };

  const onA2BSuccessHandler = async ({
    freeDeliverySection,
    statusSuccessSections,
  }: {
    freeDeliverySection?: FreeDeliverySection;
    statusSuccessSections: AddItemResponseStatus_Success_Section[];
  }) => {
    await refetchBasketSection();
    onAddToBasketSuccess?.({freeDeliverySection, statusSuccessSections});
  };

  const handleOpen = () => {
    setIsOpen(true);
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  const movRecoProducts = movUpsellingRecommendationSection.products;

  const trackOpen = () => {
    trackFeature({eventType: 'click', label: 'mov_info'});
  };

  if (movRecoProducts.products.length === 0) {
    return null;
  }

  return (
    <>
      <Wrapper data-testid="basketMovRecommendation">
        <StyledAccordion
          content={
            <HeaderWrapper>
              <TextWrapper>
                <Text>{movUpsellingRecommendationSection.headline}</Text>
              </TextWrapper>
              <ProductThumbnails
                show={!isOpen}
                products={movRecoProducts.products}
                itemsPreferences={movRecoProducts.itemPreferences}
              />
            </HeaderWrapper>
          }
          toggleContent={
            <div ref={ref}>
              <ProductList
                products={{
                  $case: 'list',
                  list: {
                    itemsPreferences:
                      movUpsellingRecommendationSection.products
                        .itemPreferences,
                    deprecatedProducts:
                      movUpsellingRecommendationSection.products.products,
                  },
                }}
                trackProductsOriginClick={trackProductsOriginClick}
                ProductTileComponent={StreamProductTileDeprecated}
                streamPosition={0}
                trackingData={{
                  sectionIndex: 0,
                  recoType:
                    movUpsellingRecommendationSection.promotionTracking.name,
                }}
                productTileConfig={{
                  addCtaVisibility: 'always',
                  onClickAddCta: onClickAddToBasket,
                  addCtaTitle: t('Add'),
                  showLowestPriorPriceHint: false,
                  gridColumns: device === 'desktop' ? 4 : 2,
                }}
                layoutConfig={{
                  constrained: device === 'desktop',
                  hideSideButtons: false,
                }}
              />
            </div>
          }
          onOpen={handleOpen}
          onClose={handleClose}
          trackOpen={trackOpen}
        />
      </Wrapper>
      <ProductMiniAdp
        close={closeFlyout}
        isOpen={isFlyoutOpen}
        productId={productId}
        selectedSize={selectedSize}
        setSelectedSize={setSelectedSize}
        onSuccess={onA2BSuccessHandler}
        sizeSelectionStateManagerKey={
          SizeSelectionOpenedStateKey.MAIN_SIZE_SELECTION
        }
        showToastMessage
        trackingData={{
          sectionIndex: 0,
          recoType: movUpsellingRecommendationSection.promotionTracking.name,
        }}
      />
    </>
  );
};

const ProductThumbnails = (props: {
  products: ProductTile[];
  itemsPreferences: ProductTilePreferences;
  show: boolean;
}) => {
  const {products, itemsPreferences, show} = props;

  const thumbnails = products.slice(0, 4).map(product => {
    return itemsPreferences?.preferredProductImageType ===
      ProductImageTypes.MODEL
      ? product.product.primaryModelImage
      : product.product.primaryBustImage;
  });
  return (
    <ImagesWrapper data-show={show}>
      {thumbnails.map(thumbnail => (
        <ImageWrapper key={thumbnail.src}>
          <ProductImage
            src={thumbnail.src}
            alt={thumbnail.description}
            padded={thumbnail.isEnclosed}
            increasePadding
          />
        </ImageWrapper>
      ))}
    </ImagesWrapper>
  );
};

const ImagesWrapper = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-direction: row;
  flex-shrink: 1;
  margin-right: 0.75rem;
  opacity: 0;
  &[data-show='true'] {
    opacity: 1;
  }
  transition: opacity 0.3s ease-in-out;
`;
const ImageWrapper = styled.div`
  width: 35px;
`;

const Wrapper = styled.div`
  margin-top: 1rem;
`;

const HeaderWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
`;

const TextWrapper = styled.div`
  flex: 1;
`;

const StyledAccordion = styled(Accordion)`
  padding: 0.5rem 1rem;
`;
